import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../Api/api';
import './AdminLogin.css';
import {
  sendForgotOtp,
  verifyForgotOtp,
  resetForgotPassword,
  handleOtpSendResponse,
  normalizeEmail,
} from '../utils/adminForgotPassword';

const PartnerLogin = () => {
  const [view, setView] = useState('login'); // login | forgot | verify | reset
  
  // Login State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // Forgot Password State
  const [forgotEmail, setForgotEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/admin/login`, { email, password });

      // Verify this is actually a partner
      if (res.data.user.account_type !== 'partner' && res.data.user.role !== 'partner') {
        toast.error('This login is for Partners only. Use /admin/login for admin access.');
        setLoading(false);
        return;
      }

      localStorage.setItem('partner_access_token', res.data.token);
      localStorage.setItem('partner_refresh_token', res.data.refresh_token);
      localStorage.setItem('partnerUser', JSON.stringify(res.data.user));
      
      toast.success('Partner login successful!');
      navigate('/partner/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    e?.preventDefault?.();
    const email = normalizeEmail(forgotEmail);
    if (!email) return toast.error('Email required');
    setLoading(true);
    try {
      const res = await sendForgotOtp(email);
      handleOtpSendResponse(res, toast);
      setForgotEmail(email);
      setView('verify');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const code = String(otp || '').trim();
    if (!code) return toast.error('OTP required');
    setLoading(true);
    try {
      await verifyForgotOtp(forgotEmail, code);
      toast.success('OTP verified');
      setView('reset');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to verify OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword) return toast.error('New password required');
    if (newPassword.length < 6) return toast.error('Password must be at least 6 characters');
    setLoading(true);
    try {
      await resetForgotPassword(forgotEmail, newPassword);
      toast.success('Password reset successfully! You can now login.');
      setView('login');
      setEmail(normalizeEmail(forgotEmail));
      setPassword('');
      setForgotEmail('');
      setOtp('');
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-container partner-login-bg">
      <div className="login-card">
        <div className="login-header">
          <div className="login-badge partner-badge">Partner</div>
          <h1>PIF Partner</h1>
          <p>{view === 'login' ? 'Sign in to your partner account' : 'Recover your partner account'}</p>
        </div>

        {view === 'login' && (
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="partner@company.com"
                required
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '15px' }}>
              <span 
                style={{ color: '#0d6efd', cursor: 'pointer', fontSize: '13px' }} 
                onClick={() => setView('forgot')}
              >
                Forgot Password?
              </span>
            </div>
            <button type="submit" disabled={loading} className="partner-submit-btn">
              {loading ? 'Signing in...' : 'Sign In as Partner'}
            </button>
          </form>
        )}

        {view === 'forgot' && (
          <form onSubmit={handleSendOtp}>
            <div className="form-group">
              <label>Account Email Address</label>
              <input 
                type="email" 
                value={forgotEmail} 
                onChange={(e) => setForgotEmail(e.target.value)} 
                placeholder="partner@company.com" 
                required 
              />
            </div>
            <button type="submit" disabled={loading} className="partner-submit-btn">
              {loading ? 'Sending...' : 'Send OTP'}
            </button>
            <div style={{ textAlign: 'center', marginTop: '15px' }}>
              <span style={{ color: '#666', cursor: 'pointer', fontSize: '14px' }} onClick={() => setView('login')}>Back to Login</span>
            </div>
          </form>
        )}

        {view === 'verify' && (
          <form onSubmit={handleVerifyOtp}>
            <div className="form-group">
              <label>Enter OTP from Email</label>
              <input 
                type="text" 
                value={otp} 
                onChange={(e) => setOtp(e.target.value)} 
                placeholder="123456" 
                required 
              />
            </div>
            <button type="submit" disabled={loading} className="partner-submit-btn">
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>
            <div style={{ textAlign: 'center', marginTop: '15px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ color: '#0d6efd', cursor: 'pointer', fontSize: '14px' }} onClick={() => handleSendOtp()}>Resend OTP</span>
              <span style={{ color: '#666', cursor: 'pointer', fontSize: '14px' }} onClick={() => setView('forgot')}>Go Back</span>
            </div>
          </form>
        )}

        {view === 'reset' && (
          <form onSubmit={handleResetPassword}>
            <div className="form-group">
              <label>New Password</label>
              <input 
                type="password" 
                value={newPassword} 
                onChange={(e) => setNewPassword(e.target.value)} 
                placeholder="••••••••" 
                required 
              />
            </div>
            <button type="submit" disabled={loading} className="partner-submit-btn">
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}

        {view === 'login' && (
          <div className="login-footer">
            <p>Partner credentials are provided by the Admin.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerLogin;
